import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  createStyles,
  fade,
  makeStyles,
  Theme
} from '@material-ui/core/styles';
import {
  ClearRounded,
  CloudUploadRounded,
  LockRounded,
  PublicRounded
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import clsx from 'clsx';
import { ReactNode, useState } from 'react';
import { FileWithPath, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { ProjectDocumentRevisionFile } from '../../api/project-document-revision-files';
import { AppDispatch } from '../../app/store';
import handleDropRejected from '../../functions/handleDropRejected';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogTitle: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(2, 2, 1, 3)
    },
    container: {
      color: theme.palette.text.hint,
      borderColor: '#dddddd',
      backgroundColor: fade(theme.palette.background.default, 0.75),
      borderRadius: theme.spacing(1),
      borderStyle: 'dashed',
      borderWidth: 'thin',
      margin: theme.spacing(0, -1),
      padding: theme.spacing(2, 5, 1, 5),
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(1, 1, 0, 1)
      },
      transition: '0.4s',
      '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: fade(theme.palette.primary.main, 0.1)
      }
    },
    containerDisable: {
      '&:hover': {
        borderColor: '#dddddd',
        backgroundColor: fade(theme.palette.background.default, 0.75)
      }
    },
    divider: {
      margin: theme.spacing(2, 0)
    },
    dividerFile: {
      marginLeft: 4,
      marginRight: 8
    },
    textCenter: {
      textAlign: 'center',
      margin: theme.spacing(1, 2)
    },
    textCaption: {
      marginTop: theme.spacing(2)
    },
    flexGrow: {
      flexGrow: 1
    },
    cursorPointer: {
      cursor: 'pointer'
    },
    filenameFlex: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 8
    },
    filename: {
      wordBreak: 'break-all',
      paddingRight: 8
    },
    alertBox: {
      margin: theme.spacing(0, -1, 1, -1)
    }
  })
);

interface UploadFileProps {
  title?: string;
  open: boolean;
  onClose: () => void;
  multiple?: boolean;
  onSubmit: (files: ProjectDocumentRevisionFile[]) => void;
  canChangePrivacy?: boolean;
  filename?: string;
  loading: boolean;
  dispatch: AppDispatch;
}

export default function UploadFile({
  title,
  open,
  onClose,
  multiple = false,
  onSubmit,
  canChangePrivacy = false,
  filename,
  loading,
  dispatch
}: UploadFileProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const defaultTitle = t('uploadFile.uploadFile') as string;

  const MAX_SIZE_IN_MB = Number.parseInt(
    process.env.REACT_APP_UPLOAD_FILE_SIZE || '25',
    10
  );
  const [alertFileType, setAlertFileType] = useState<boolean>(false);
  const [files, setFiles] = useState<ProjectDocumentRevisionFile[]>([]);
  const { getRootProps, getInputProps } = useDropzone({
    onDropAccepted,
    onDropRejected: (fileRejections) =>
      handleDropRejected(fileRejections, dispatch, MAX_SIZE_IN_MB),
    // To select type of upload file add 'accept' property e.g. accept: '.pdf'
    maxSize: MAX_SIZE_IN_MB * 1048576, //maxSize per file is 250 MB
    multiple
  });

  const fileList = (files: ProjectDocumentRevisionFile[]): ReactNode =>
    files.map(({ file, isPublic }, index) => {
      const fileSizeinKB = Math.round(file.size / 1024);
      const fileSizeinMB = Math.round((file.size * 10) / 1048576) / 10;
      const fileSize =
        file.size > 1048576 ? `${fileSizeinMB}MB` : `${fileSizeinKB}KB`;
      const buttonName = isPublic ? t('uploadFile.public') as string : t('uploadFile.encOnly') as string;
      return (
        <div className={classes.filenameFlex} key={index}>
          {canChangePrivacy && (
            <>
              <Tooltip title={buttonName} arrow>
                <IconButton
                  aria-label={buttonName}
                  color="primary"
                  size="small"
                  onClick={() => {
                    onChangePublic(file.path as string);
                  }}
                >
                  {isPublic ? <PublicRounded /> : <LockRounded />}
                </IconButton>
              </Tooltip>
              <Divider
                className={classes.dividerFile}
                orientation="vertical"
                flexItem
              />
            </>
          )}
          <Typography className={classes.filename} variant="body2">
            {`${file.path} (${fileSize})`}
          </Typography>
          <IconButton
            aria-label={t('uploadFile.deselectFile') as string}
            size="small"
            onClick={() => onFilterOut(file.path as string)}
          >
            <ClearRounded />
          </IconButton>
        </div>
      );
    });

  function onDropAccepted(acceptedFiles: FileWithPath[]) {
    setAlertFileType(false);
    if (multiple) {
      const previousFiles = files.filter(
        ({ file }) =>
          !acceptedFiles.some((acceptedFile) => acceptedFile.path === file.path)
      );
      const newAcceptedFiles = acceptedFiles.map((acceptedFile) => {
        return { file: acceptedFile, isPublic: true };
      });
      setFiles([...previousFiles, ...newAcceptedFiles]);
    } else {
      const [file] = acceptedFiles;
      if (!filename || filename === file.name) {
        setFiles([{ file, isPublic: true }]);
      } else {
        setAlertFileType(true);
      }
    }
  }

  function onFilterOut(path: string) {
    const newFiles = files.filter(({ file }) => file.path !== path);
    setFiles(newFiles);
  }

  function onChangePublic(path: string) {
    const fileIndex = files.findIndex(({ file }) => file.path === path);
    const newFile = {
      ...files[fileIndex],
      isPublic: !files[fileIndex].isPublic
    };
    setFiles([
      ...files.slice(0, fileIndex),
      newFile,
      ...files.slice(fileIndex + 1)
    ]);
  }

  function onCloseResetFiles() {
    onClose();
    setAlertFileType(false);
    setFiles([]);
  }

  function onSubmitFiles() {
    onSubmit(files);
    setAlertFileType(false);
    setFiles([]);
  }

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onCloseResetFiles}>
      <div className={classes.dialogTitle}>
        <Typography className={classes.flexGrow} variant="h6">
          {title || defaultTitle}
        </Typography>
        <IconButton size="small" onClick={onCloseResetFiles}>
          <ClearRounded />
        </IconButton>
      </div>
      <DialogContent dividers>
        {canChangePrivacy && (
          <Alert className={classes.alertBox} severity="info">
            {t('uploadFile.publicFilesInfo') as string}
          </Alert>
        )}
        {filename && alertFileType && (
          <Alert className={classes.alertBox} severity="error">
            {t('uploadFile.sameNameFormatError') as string}
          </Alert>
        )}
        <div
          {...getRootProps({ className: 'dropzone' })}
          className={clsx(classes.container, {
            [classes.containerDisable]: loading
          })}
        >
          <input
            data-testid="file-upload"
            {...getInputProps()}
            disabled={loading}
          />
          <div className={classes.textCenter}>
            <Typography variant="body2">
              {multiple
                ? t('uploadFile.dragDropMultiple') as string
                : t('uploadFile.dragDropSingle') as string}
            </Typography>
            <Typography
              className={classes.cursorPointer}
              variant="button"
              color={loading ? 'textSecondary' : 'primary'}
            >
              <b>{t('uploadFile.browse') as string}</b>
            </Typography>
            <div className={classes.textCaption}>
              <Typography variant="caption">
                {t('uploadFile.maxSize', { size: MAX_SIZE_IN_MB }) as string}
              </Typography>
            </div>
          </div>
        </div>
        {files.length > 0 && (
          <>
            <Divider className={classes.divider} />
            <Typography variant="body1" gutterBottom>
              {multiple ? t('uploadFile.files') as string : t('uploadFile.file') as string}
            </Typography>
            {fileList(files)}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onSubmitFiles}
          color="primary"
          variant="contained"
          fullWidth
          disabled={files.length === 0 || loading}
          startIcon={<CloudUploadRounded />}
        >
          {t('uploadFile.uploadFile') as string}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
